import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "./AuthContext";
import { BLOG_MANAGEMENT_HEAD_OFFICE_ID, panelKeyForPath, panelByKey, PANEL_CATALOG } from "../config/panelCatalog";
import { getMyPermissions } from "../api/access";
import { sessionGet } from "../utils/sessionStore";

type PanelLevel = "view" | "manage";
interface PanelPermission {
  visible: boolean;
  level: PanelLevel;
}

interface AccessClaim {
  hasAccess: boolean;
  accessName: string | null;
  permissions: Record<string, PanelPermission>;
}

interface AccessContextValue extends AccessClaim {
  loading: boolean;
  isPanelVisible: (key: string) => boolean;
  isPathVisible: (pathname: string) => boolean;
  canManage: (key: string) => boolean;
  firstVisiblePath: () => string;
  refresh: () => void;
}

// Read the Access profile straight from the JWT (the backend embeds it as the
// `access` claim at login). No API call. Absent/invalid claim → permissive.
function readAccessFromToken(): AccessClaim {
  try {
    const token = sessionGet("accessToken");
    if (!token) return { hasAccess: false, accessName: null, permissions: {} };
    const decoded: any = jwtDecode(token);
    const a = decoded?.access;
    if (a && a.has_access) {
      return {
        hasAccess: true,
        accessName: a.access_name ?? null,
        permissions: (a.permissions as Record<string, PanelPermission>) || {},
      };
    }
    // No profile is still permissive, but keep any key the backend named
    // anyway: a per-employee Meetly grant/revoke rides here with has_access
    // false, and dropping it would show a panel the API refuses (or hide one
    // it allows).
    if (a?.permissions) {
      return {
        hasAccess: false,
        accessName: null,
        permissions: (a.permissions as Record<string, PanelPermission>) || {},
      };
    }
  } catch {
    /* malformed token → fall back to permissive */
  }
  return { hasAccess: false, accessName: null, permissions: {} };
}

const DEFAULT: AccessContextValue = {
  loading: false,
  hasAccess: false,
  accessName: null,
  permissions: {},
  isPanelVisible: () => true,
  isPathVisible: () => true,
  canManage: () => true,
  firstVisiblePath: () => "/dashboard",
  refresh: () => {},
};

const AccessContext = createContext<AccessContextValue>(DEFAULT);

export const useAccess = () => useContext(AccessContext);

export const AccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [claim, setClaim] = useState<AccessClaim>(() => readAccessFromToken());

  // The JWT claim is only the instant/offline fallback: it's baked in at login,
  // so an admin's change to an Access profile wouldn't reach an already-logged-in
  // user until they re-login. To make changes take effect on the next page load,
  // reconcile the token claim with the *live* profile from /access/my-permissions.
  const refresh = useCallback(() => {
    setClaim(readAccessFromToken()); // instant: no flash, covers the pre-fetch window
    if (!sessionGet("accessToken")) return; // logged out → nothing to fetch
    getMyPermissions()
      .then((p) =>
        setClaim({
          hasAccess: !!p.has_access,
          accessName: p.access_name ?? null,
          permissions: (p.permissions as Record<string, PanelPermission>) || {},
        })
      )
      .catch(() => {
        /* network/auth hiccup → keep the token-derived claim */
      });
  }, []);

  // Re-read whenever auth state flips (login / logout) and on first mount.
  useEffect(() => {
    refresh();
  }, [isAuthenticated, refresh]);

  const value = useMemo<AccessContextValue>(() => {
    const { hasAccess, permissions } = claim;
    const isPanelVisible = (key: string) => {
      if (key === "set-blog-management" && sessionGet("headOfficeID") !== BLOG_MANAGEMENT_HEAD_OFFICE_ID) return false;
      if (user?.role === "admin" && key === "set-blog-management") return true;
      const def = panelByKey(key);
      // View is forced on for every role (e.g. Company Policy) — never hidden.
      if (def?.forceView) return true;
      const perm = permissions[key];
      // No profile → permissive, EXCEPT for a key the server stated outright.
      // Only a per-employee Meetly override lands here today.
      if (!hasAccess) return perm ? perm.visible === true : true;
      // A profile saved before this panel existed won't list the key at all.
      // Fall back to the catalog's default-visible flag so opt-in-visible panels
      // (e.g. Documents) aren't hidden just because the profile predates them.
      // An explicit `false` in the profile is still respected (stays hidden).
      if (!perm) return def?.defaultVisible === true;
      return perm.visible === true;
    };
    const isPathVisible = (pathname: string) => {
      const key = panelKeyForPath(pathname);
      if (!key) return true; // not a gated panel (settings, calendar, etc.)
      return isPanelVisible(key);
    };
    const canManage = (key: string) => {
      if (user?.role === "admin" && key === "set-blog-management") return true;
      if (!hasAccess) return true;
      const p = permissions[key];
      // Profile saved before this panel/feature existed → use the catalog's
      // declared default instead of silently denying, mirroring isPanelVisible.
      if (!p) {
        const def = panelByKey(key);
        return def?.defaultVisible === true && def?.defaultLevel === "manage";
      }
      return !!p.visible && p.level === "manage";
    };
    const firstVisiblePath = () => {
      if (!hasAccess) return "/dashboard";
      const found = PANEL_CATALOG.find(
        (p) => !p.feature && p.teamlioPath && permissions[p.key]?.visible === true
      );
      return found ? found.teamlioPath : "/dashboard";
    };
    return {
      loading: false,
      ...claim,
      isPanelVisible,
      isPathVisible,
      canManage,
      firstVisiblePath,
      refresh,
    };
  }, [claim, refresh, user?.role]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};
